import React, { createContext, useState, useEffect } from "react";
import { msalInstance, loginRequest } from "./msalConfig";
import { InteractionStatus, BrowserAuthError } from "@azure/msal-browser";

/* =====================================================
   Context
===================================================== */
export const UserContext = createContext(null);

/* =====================================================
   Configuration
===================================================== */

// DEV detection (shared rule across the app)
const isDevEmail = (email) =>
  typeof email === "string" && email.endsWith("@dev.edgeneweb");

// Always call backend explicitly
const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5000";

/* =====================================================
   Provider
===================================================== */
export const UserProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userAccount, setUserAccount] = useState(null);

  // Unified user status for app routing
  const [userStatus, setUserStatus] = useState({
    plan: null,
    voucherStatus: "unknown",
  });

  const [isStatusLoading, setIsStatusLoading] = useState(true);

  /* =====================================================
     DEV MODE SHORT‑CIRCUIT
     No MSAL, no tokens, no Azure
  ===================================================== */
  useEffect(() => {
    if (isDevEmail(userAccount?.email)) {
      setIsAuthenticated(true);
      setUserStatus({
        plan: userAccount?.plan || null,
        voucherStatus: "dev",
      });
      setIsStatusLoading(false);
    }
  }, [userAccount]);

  /* =====================================================
     PROD AUTH + STATUS (MSAL)
  ===================================================== */
  useEffect(() => {
    // DEV must never trigger MSAL
    if (userAccount && isDevEmail(userAccount.email)) {
      return;
    }

    const fetchUserStatus = async (account) => {
      if (!account) {
        setUserStatus({ plan: null, voucherStatus: "no_account" });
        setIsStatusLoading(false);
        return;
      }

      try {
        const tokenResponse =
          await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
          });

        const res = await fetch(`${API_BASE}/api/user/status`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        setUserStatus(data);
      } catch (err) {
        console.error("Error fetching user status:", err);
        setUserStatus({ plan: null, voucherStatus: "error" });
      } finally {
        setIsStatusLoading(false);
      }
    };

    const handleAuth = async () => {
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        const account = accounts[0];
        setIsAuthenticated(true);
        setUserAccount(account);
        await fetchUserStatus(account);
      } else {
        setIsAuthenticated(false);
        setUserAccount(null);
        setUserStatus({ plan: null, voucherStatus: "no_account" });
        setIsStatusLoading(false);
      }
    };

    const callbackId = msalInstance.addEventCallback((message) => {
      if (
        message.eventType === "msal:loginSuccess" ||
        message.eventType === "msal:logoutSuccess"
      ) {
        handleAuth();
      }
    });

    handleAuth();

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  /* =====================================================
     Public API
  ===================================================== */

  // Unified login:
  // DEV → local state only
  // PROD → MSAL popup
  const login = async (
    email,
    name,
    surname,
    plan = null,
    voucherUsed = false
  ) => {
    // DEV LOGIN
    if (isDevEmail(email)) {
      setUserAccount({
        email,
        name,
        surname,
        plan,
        voucherUsed,
      });
      setIsAuthenticated(true);
      setUserStatus({
        plan,
        voucherStatus: voucherUsed ? "used" : "available",
      });
      setIsStatusLoading(false);
      return;
    }

    // PROD LOGIN (MSAL)
    try {
      if (
        msalInstance.getInteractionStatus() !==
        InteractionStatus.None
      ) {
        console.warn(
          "Authentication already in progress."
        );
        return;
      }

      await msalInstance.loginPopup(loginRequest);
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "user_cancelled"
      ) {
        console.log("MSAL login cancelled by user.");
      } else {
        console.error("MSAL login failed:", error);
      }
    }
  };

  const logout = () => {
    if (userAccount && isDevEmail(userAccount.email)) {
      setIsAuthenticated(false);
      setUserAccount(null);
      setUserStatus({ plan: null, voucherStatus: "logged_out" });
      return;
    }

    msalInstance.logoutRedirect();
  };

  /* =====================================================
     Context Value
  ===================================================== */
  const value = {
    isAuthenticated,
    userAccount,
    userStatus,
    isStatusLoading,
    login,
    logout,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};